// Order Sheet › Parts: the parts the season's boats need, summed by SAP part code. Each boat's BOM is worked
// out as its order file does (the model's standard sections and the option sections its Order Sheet row calls
// for, with the boat's own ticks), from the BOM lists; pick the boats to count (by default the ones with an
// order file), then take the totals to Excel or as an SAP import file.

import { useMemo, useState } from 'react';
import { MODEL_ORDER, bomLines, modelKey, orderKeySap, partTotals, resolveBom, type BomList, type BomLine } from '@technohull/shared';
import { useOrderSheet, useOverview } from '../features/hooks.js';
import { bomSeasonFor, useBom, useBomLists, useOrderFiles } from '../features/bom.js';
import { fold, hasText } from '../lib/search.js';
import { EmptyState, ErrorState, LoadingState, SearchInput } from './ui.js';

export function OrderSheetParts({ season }: { season: string }) {
  const overview = useOverview(season || undefined);
  const sheetQ = useOrderSheet();
  const filesQ = useOrderFiles(season);
  const bomQ = useBom();
  const [chosen, setChosen] = useState<Set<string> | null>(null);
  const [model, setModel] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<'code' | 'qty'>('code');

  // the boats a BOM can be worked out for: on the season's Overview, with an order or an order file, and a
  // list for their model
  const boats = useMemo(() => {
    if (!overview.data || !sheetQ.data || !filesQ.data || !bomQ.data) return [];
    const files = new Map(filesQ.data.map((f) => [f.sap, f]));
    const listed = new Set(overview.data.vessels.map((v) => v.sap));
    return [
      ...overview.data.vessels
        .filter((v) => v.sap && !v.left && (files.has(v.sap) || sheetQ.data!.sheet[v.sap]))
        .map((v) => ({ sap: v.sap, name: v.name, model: modelKey(v.model), file: files.get(v.sap) })),
      // boats not on the Overview that have an order file (a stock boat, one not in Smartsheet yet)
      ...filesQ.data.filter((f) => !listed.has(f.sap)).map((f) => ({ sap: f.sap, name: f.name, model: f.model, file: f })),
    ]
      .map((b) => ({ ...b, bomSeason: bomSeasonFor(bomQ.data!.lists, b.model, season) }))
      .filter((b) => b.bomSeason)
      .sort((a, b) => MODEL_ORDER.indexOf(a.model as never) - MODEL_ORDER.indexOf(b.model as never) || a.name.localeCompare(b.name));
  }, [overview.data, sheetQ.data, filesQ.data, bomQ.data, season]);
  const picked = chosen ?? new Set(boats.filter((b) => b.file).map((b) => b.sap));

  const pairs = useMemo(() => [...new Map(boats.map((b) => [`${b.bomSeason}/${b.model}`, { season: b.bomSeason, model: b.model }])).values()], [boats]);
  const listQs = useBomLists(pairs);
  const lists = useMemo(() => {
    const m = new Map<string, BomList>();
    listQs.forEach((r, i) => { if (r.data) m.set(`${pairs[i]!.season}/${pairs[i]!.model}`, r.data); });
    return m;
  }, [listQs, pairs]);
  const listsLoading = listQs.some((r) => r.isLoading);

  const counted = useMemo(() => {
    const out: Array<{ sap: string; name: string; model: string; lines: BomLine[] }> = [];
    for (const b of boats) {
      if (!picked.has(b.sap)) continue;
      const list = lists.get(`${b.bomSeason}/${b.model}`);
      if (!list) continue;
      const values = sheetQ.data?.sheet[b.sap] ?? {};
      const lines = bomLines(resolveBom(list, values, bomQ.data?.config.links[b.model] ?? {}, b.file?.picks ?? {}));
      out.push({ sap: b.sap, name: b.name, model: b.model, lines });
    }
    return out;
  }, [boats, picked, lists, sheetQ.data, bomQ.data]);
  const totals = useMemo(() => partTotals(counted.map((c) => c.lines)), [counted]);
  const needle = fold(q.trim());
  const rows = useMemo(() => {
    const r = needle ? totals.filter((t) => hasText(needle, t.code, t.desc, t.pos)) : totals;
    return sort === 'qty' ? [...r].sort((a, b) => b.qty - a.qty) : r;
  }, [totals, needle, sort]);

  const err = overview.error ?? sheetQ.error ?? filesQ.error ?? bomQ.error;
  if (err) return <ErrorState error={err} />;
  if (!overview.data || !sheetQ.data || !filesQ.data || !bomQ.data) return <LoadingState />;
  if (!bomQ.data.lists.length) return <EmptyState title="No BOM lists yet" hint="Upload the STD lists on Management › Records › BOM lists, then each boat’s parts are counted here." />;
  if (!boats.length) return <EmptyState title="No boats to count yet" hint="A boat is counted once it has an order (an order file or an Order Sheet row) and its model has a BOM list." />;

  const toggle = (sap: string) => setChosen(() => { const n = new Set(picked); if (n.has(sap)) n.delete(sap); else n.add(sap); return n; });
  const inModel = boats.filter((b) => !model || b.model === model);
  const setAll = (on: boolean) => setChosen(() => { const n = new Set(picked); for (const b of inModel) { if (on) n.add(b.sap); else n.delete(b.sap); } return n; });
  const fileBase = `Parts ${season}`;

  return (
    <div className="osp">
      <details className="card osp-boats">
        <summary><b>Boats</b> <span className="muted">· {counted.length} of {boats.length} counted (those with an order file to start with)</span></summary>
        <div className="toolbar osp-models">
          <button className={`btn btn-sm ${!model ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setModel('')}>All</button>
          {MODEL_ORDER.filter((m) => boats.some((b) => b.model === m)).map((m) => (
            <button key={m} className={`btn btn-sm ${model === m ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setModel(m)}>{m}</button>
          ))}
          <span className="osp-gap" />
          <button className="btn btn-sm btn-ghost" onClick={() => setAll(true)}>Tick all{model ? ` ${model}` : ''}</button>
          <button className="btn btn-sm btn-ghost" onClick={() => setAll(false)}>Untick all{model ? ` ${model}` : ''}</button>
        </div>
        <ul className="osp-list">
          {inModel.map((b) => (
            <li key={b.sap}>
              <label className="check-row">
                <input type="checkbox" checked={picked.has(b.sap)} onChange={() => toggle(b.sap)} />
                <span className="osp-m">{b.model}</span> {b.name} <span className="muted">{orderKeySap(b.sap) || 'no SAP'}</span>
                {!b.file && <span className="muted"> · Order Sheet only</span>}
              </label>
            </li>
          ))}
        </ul>
      </details>

      <div className="toolbar osp-bar">
        <SearchInput value={q} onChange={setQ} placeholder="SAP code, part or position" width={260} />
        <label className="check-row"><input type="checkbox" checked={sort === 'qty'} onChange={(e) => setSort(e.target.checked ? 'qty' : 'code')} /> Most needed first</label>
        <span className="osp-gap" />
        <span className="muted">{totals.length} parts{listsLoading ? ' · loading lists…' : ''}</span>
        <button className="btn btn-sm" disabled={!totals.length} onClick={async () => { const { exportPartTotals } = await import('../features/orderFileExport.js'); await exportPartTotals(`${fileBase}.xlsx`, totals, counted.map((c) => ({ sap: orderKeySap(c.sap), name: c.name, model: c.model, lines: c.lines.length }))); }}>Excel</button>
        <button className="btn btn-sm" disabled={!totals.length} onClick={async () => { const { downloadSapCsv } = await import('../features/orderFileExport.js'); downloadSapCsv(`${fileBase} - SAP.csv`, totals); }}>SAP file</button>
      </div>

      <div className="tbl-wrap">
        <table className="tbl osp-tbl">
          <thead><tr><th>SAP code</th><th>Part</th><th>Position</th><th className="num">Qty</th><th className="num">Boats</th></tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.code || `d:${t.desc}`}>
                <td className="mono">{t.code || <span className="muted" title="No SAP code on the STD list yet">-</span>}</td>
                <td>{t.desc}</td>
                <td className="muted">{t.pos}</td>
                <td className="num"><b>{t.qty}</b>{t.odd > 0 && <span className="muted" title={`${t.odd} line(s) without a number`}> +{t.odd}?</span>}</td>
                <td className="num">{t.boats}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 16 }}>{counted.length ? 'No parts match.' : 'Tick some boats to count their parts.'}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
